import { createReducer, on } from '@ngrx/store';
import { DrawingMessage } from '../../../shared/models/drawing-message.model';
import {
  loadAllDrawingMessages,
  loadAllDrawingMessagesSuccess,
  loadAllDrawingMessagesFailure,
  loadDrawingMessages,
  loadDrawingMessagesSuccess,
  loadDrawingMessagesFailure,
  createDrawingMessageSuccess,
  deleteDrawingMessageSuccess,
  updateDrawingMessage,
  updateDrawingMessageSuccess,
} from './drawing-message.action';
import { StateStatus } from '../../../shared/models/state-status.models';

export interface DrawingMessageState {
  messagesByDrawingId: Record<string, DrawingMessage[]>;
  loadStatus: StateStatus;
  error: any;
}

export const initialState: DrawingMessageState = {
  messagesByDrawingId: {},
  loadStatus: 'pending',
  error: null,
};

export const drawingMessageReducer = createReducer(
  initialState,
  on(loadAllDrawingMessages, (state): DrawingMessageState => ({
    ...state,
    loadStatus: 'loading' as StateStatus,
  })),
  on(loadAllDrawingMessagesSuccess, (state, { messagesByDrawingId }): DrawingMessageState => ({
    ...state,
    messagesByDrawingId: { ...state.messagesByDrawingId, ...messagesByDrawingId },
    loadStatus: 'loadSuccess' as StateStatus,
  })),
  on(loadAllDrawingMessagesFailure, (state, { error }): DrawingMessageState => ({
    ...state,
    error,
    loadStatus: 'error' as StateStatus,
  })),
  on(loadDrawingMessages, (state): DrawingMessageState => ({
    ...state,
    loadStatus: 'loading' as StateStatus,
  })),
  on(loadDrawingMessagesSuccess, (state, { drawingId, messages }): DrawingMessageState => ({
    ...state,
    messagesByDrawingId: { ...state.messagesByDrawingId, [drawingId]: messages },
    loadStatus: 'loadSuccess' as StateStatus,
  })),
  on(loadDrawingMessagesFailure, (state, { error }): DrawingMessageState => ({
    ...state,
    error,
    loadStatus: 'error' as StateStatus,
  })),
  on(createDrawingMessageSuccess, (state, { drawingId, message }): DrawingMessageState => ({
    ...state,
    messagesByDrawingId: {
      ...state.messagesByDrawingId,
      [drawingId]: [...(state.messagesByDrawingId[drawingId] ?? []), message],
    },
  })),
  on(deleteDrawingMessageSuccess, (state, { id, drawingId }): DrawingMessageState => ({
    ...state,
    messagesByDrawingId: {
      ...state.messagesByDrawingId,
      [drawingId]: (state.messagesByDrawingId[drawingId] ?? []).filter(m => m.id !== id),
    },
  })),
  on(updateDrawingMessage, (state, { id, drawingId, message }): DrawingMessageState => ({
    ...state,
    messagesByDrawingId: {
      ...state.messagesByDrawingId,
      [drawingId]: (state.messagesByDrawingId[drawingId] ?? []).map(m =>
        m.id === id ? { ...m, message, isEdited: true, updatedOn: new Date().toISOString() } : m
      ),
    },
  })),
  on(updateDrawingMessageSuccess, (state, { id, drawingId, message, removedAttachmentIds, addedAttachments }): DrawingMessageState => ({
    ...state,
    messagesByDrawingId: {
      ...state.messagesByDrawingId,
      [drawingId]: (state.messagesByDrawingId[drawingId] ?? []).map(m => {
        if (m.id !== id) return m;
        const removed = removedAttachmentIds ?? [];
        const added = addedAttachments ?? [];
        const keptAttachments = (m.attachments ?? []).filter(a => !removed.includes(a.id));
        return { ...m, message, attachments: [...keptAttachments, ...added] };
      }),
    },
  })),
);
